/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin, notification } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import get from 'lodash/get';
import {
  DAILY_AI_FEATURE_IDS,
  SPACER_LOG_AI_FEATURES,
  fetchAiInsightFeatures,
  generateAiInsight,
  getAskEvansUnavailability,
  cacheUsageForFeedbackTarget,
  buildSpacerLogAiRequestBody,
} from '../../services/aiInsightsApi';
import SpacerLogMarkdown from './SpacerLogMarkdown';
import SpacerLogAiCitations from './SpacerLogAiCitations';
import SpacerLogAiAnswerActions from './SpacerLogAiAnswerActions';
import SpacerLogAiInsightsSidebar from './SpacerLogAiInsightsSidebar';

/** Display titles for sidebar (API titles may be shorter). */
const FEATURE_DISPLAY = {
  [SPACER_LOG_AI_FEATURES.PERIOD_BRIEF]: {
    title: 'Period management brief',
    description: 'Month/quarter/year narrative from overview KPIs and pipeline comparison.',
  },
  [SPACER_LOG_AI_FEATURES.SHIFT_BRIEF]: {
    title: 'Shift / day superintendent brief',
    description: 'Narrative summary of daily KPIs, issues, bottlenecks, and actions.',
  },
  [SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH]: {
    title: 'Bottleneck / pace coach',
    description: 'Coaching on joint stage pace and bottlenecks.',
  },
  [SPACER_LOG_AI_FEATURES.COMMENT_THEMES]: {
    title: 'Downtime & comment themes',
    description: 'Themes across downtime comments for the day.',
  },
  [SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT]: {
    title: 'Close-out / compliance assistant',
    description: 'Compliance gaps for the selected day in plain language.',
  },
  [SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN]: {
    title: 'Data-quality anomaly explain',
    description: 'Explain unusual joint or KPI patterns for the day.',
  },
};

const FALLBACK_FEATURES = [
  {
    feature: SPACER_LOG_AI_FEATURES.PERIOD_BRIEF,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.PERIOD_BRIEF],
    maturity: 'P0',
    requiredContext: ['projectId', 'anchorDate', 'periodType'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.SHIFT_BRIEF,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.SHIFT_BRIEF],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMMENT_THEMES,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.COMMENT_THEMES],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
];

const withDisplayMeta = (f) => {
  const display = FEATURE_DISPLAY[f.feature];
  if (!display) return f;
  return {
    ...f,
    title: display.title,
    description: f.description || display.description,
  };
};

const sessionCache = new Map();

const cacheKey = (feature, ctx) =>
  [
    feature,
    ctx.projectId,
    ctx.sowId ?? '',
    ctx.logDate ?? '',
    ctx.shift ?? '',
    ctx.anchorDate ?? '',
    ctx.periodType ?? '',
  ].join(':');

const missingContext = (required = [], ctx) => {
  const missing = [];
  required.forEach((key) => {
    const val = ctx[key];
    if (val == null || val === '') {
      missing.push(key);
      return;
    }
    if (key === 'sowId' && (val === 'all' || Number.isNaN(Number(val)))) {
      missing.push('sowId (select a pipeline)');
    }
  });
  return missing;
};

/**
 * Insights briefs — same Ask Evans chat shell (sidebar + messages + composer).
 */
const SpacerLogAiInsightPanel = ({
  open,
  initialFeature,
  tabScope = 'daily',
  context = {},
  onQuota,
  onExportMeta,
  quotaBar = null,
  children,
}) => {
  const [featuresMeta, setFeaturesMeta] = useState(null);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(
    initialFeature || SPACER_LOG_AI_FEATURES.SHIFT_BRIEF
  );
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);

  const aiEnabled = featuresMeta ? featuresMeta.enabled !== false : true;
  const askEvansBlock = useMemo(
    () => getAskEvansUnavailability(featuresMeta),
    [featuresMeta]
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingFeatures(true);
    fetchAiInsightFeatures()
      .then((data) => {
        if (!cancelled) {
          setFeaturesMeta(data);
          if (typeof onQuota === 'function' && data.quota) onQuota(data.quota);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFeaturesMeta((prev) => ({
            ...(prev || {}),
            enabled: prev?.enabled !== false,
            ragConfigured: prev?.ragConfigured ?? false,
            features: prev?.features?.length ? prev.features : FALLBACK_FEATURES,
            featuresLoadError: true,
          }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFeatures(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, onQuota]);

  useEffect(() => {
    if (open && initialFeature) {
      setSelectedFeature(initialFeature);
      setResult(null);
    }
  }, [open, initialFeature]);

  useEffect(() => {
    if (!open) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setGenerating(false);
      setElapsedSec(0);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [result, generating]);

  const scopedFeatures = useMemo(() => {
    const apiList = featuresMeta?.features?.length ? featuresMeta.features : FALLBACK_FEATURES;
    const allow =
      tabScope === 'overview'
        ? new Set([SPACER_LOG_AI_FEATURES.PERIOD_BRIEF])
        : new Set(DAILY_AI_FEATURE_IDS);
    return apiList.filter((f) => allow.has(f.feature)).map(withDisplayMeta);
  }, [featuresMeta, tabScope]);

  useEffect(() => {
    if (!scopedFeatures.length) return;
    if (!scopedFeatures.some((f) => f.feature === selectedFeature)) {
      setSelectedFeature(scopedFeatures[0].feature);
    }
  }, [scopedFeatures, selectedFeature]);

  useEffect(() => {
    if (!open || !selectedFeature) return;
    const key = cacheKey(selectedFeature, context);
    if (sessionCache.has(key)) {
      const cached = sessionCache.get(key);
      setResult(cached);
      if (typeof onQuota === 'function' && cached?.quota) onQuota(cached.quota);
    }
    // Intentionally key off primitive context fields (parent builds a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedFeature,
    context.projectId,
    context.sowId,
    context.logDate,
    context.shift,
    context.anchorDate,
    context.periodType,
  ]);

  const selectedMeta = scopedFeatures.find((f) => f.feature === selectedFeature) || null;
  const isStub = selectedMeta?.status === 'stub';
  const ctxMissing = missingContext(selectedMeta?.requiredContext || [], context);

  const pushExportMeta = useCallback(
    (patch) => {
      if (typeof onExportMeta !== 'function') return;
      onExportMeta(patch);
    },
    [onExportMeta]
  );

  useEffect(() => {
    if (!open) {
      pushExportMeta({
        chatSessionId: null,
        hasMarkdown: false,
        generating: false,
        fallbackMessages: null,
        title: null,
      });
    }
  }, [open, pushExportMeta]);

  useEffect(() => {
    if (!result?.markdown) {
      pushExportMeta({
        hasMarkdown: false,
        fallbackMessages: null,
        title: selectedMeta?.title || null,
      });
      return;
    }
    const briefTitle = selectedMeta?.title || 'Insight brief';
    pushExportMeta({
      chatSessionId: result.chatSessionId || null,
      hasMarkdown: true,
      generating: false,
      title: briefTitle,
      fallbackMessages: [
        { role: 'user', text: briefTitle },
        { role: 'assistant', markdown: result.markdown },
      ],
    });
  }, [result, selectedMeta?.title, pushExportMeta]);

  const buildBody = useCallback(
    (mode) =>
      buildSpacerLogAiRequestBody({
        context,
        feature: selectedFeature,
        mode,
        includePeriodScope: tabScope === 'overview',
        includeDashboardFilters: true,
      }),
    [selectedFeature, context, tabScope]
  );

  const runGenerate = useCallback(
    async (mode = 'ai') => {
      if (!selectedFeature || ctxMissing.length) return;
      if (mode === 'ai' && !aiEnabled) {
        mode = 'template';
      }
      if (isStub && mode === 'ai') {
        notification.info({ message: 'This insight is coming soon.' });
        return;
      }

      const key = cacheKey(selectedFeature, context);
      if (mode === 'ai' && sessionCache.has(key)) {
        const cached = sessionCache.get(key);
        setResult(cached);
        if (typeof onQuota === 'function' && cached?.quota) onQuota(cached.quota);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setGenerating(true);
      pushExportMeta({ generating: true });
      setElapsedSec(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);

      try {
        const data = await generateAiInsight(buildBody(mode), { signal: controller.signal });
        setResult(data);
        if (typeof onQuota === 'function' && data?.quota) onQuota(data.quota);
        if (data?.feedbackTargetId && data?.usage) {
          cacheUsageForFeedbackTarget(data.feedbackTargetId, data.usage);
        }
        if (mode === 'ai' && data?.markdown) {
          sessionCache.set(key, data);
        }
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
          return;
        }
        const msg = get(err, 'error', get(err, 'message', 'Failed to generate insight.'));
        notification.error({ message: msg });
      } finally {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setGenerating(false);
        pushExportMeta({ generating: false });
        abortRef.current = null;
      }
    },
    [selectedFeature, ctxMissing.length, aiEnabled, isStub, context, buildBody, onQuota, pushExportMeta]
  );

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleSelectFeature = (featureId) => {
    if (!featureId || featureId === selectedFeature) return;
    setSelectedFeature(featureId);
    setResult(null);
  };

  // On open, generate with current Spacer Log date range / pipeline context.
  useEffect(() => {
    if (!open || !selectedFeature || ctxMissing.length || generating || result?.markdown) return undefined;
    const key = cacheKey(selectedFeature, context);
    if (sessionCache.has(key)) {
      const cached = sessionCache.get(key);
      setResult(cached);
      if (typeof onQuota === 'function' && cached?.quota) onQuota(cached.quota);
      return undefined;
    }
    const timer = setTimeout(() => {
      runGenerate(aiEnabled ? 'ai' : 'template');
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedFeature,
    ctxMissing.length,
    context.projectId,
    context.sowId,
    context.logDate,
    context.shift,
    context.anchorDate,
    context.periodType,
    context.fromDate,
    context.toDate,
  ]);

  const contextHint = ctxMissing.length
    ? tabScope === 'daily' && ctxMissing.some((m) => String(m).includes('sowId'))
      ? 'Select a pipeline day first.'
      : `Missing context: ${ctxMissing.join(', ')}`
    : null;

  if (!open) return null;

  const showFallbackBanner =
    result && (result.fallback === true || result.mode === 'template');

  const sidebar = (
    <SpacerLogAiInsightsSidebar
      features={scopedFeatures}
      activeFeature={selectedFeature}
      loading={loadingFeatures && !scopedFeatures.length}
      onSelect={handleSelectFeature}
    />
  );

  const main = (
    <div className="spacer-ask-evans__main">
      <div className="spacer-ask-evans__toolbar">
        <div className="spacer-ask-evans__modes" role="status">
          <span className="spacer-ask-evans__mode spacer-ask-evans__mode--active">
            {selectedMeta?.title || 'Insight brief'}
          </span>
        </div>
        {result?.markdown ? (
          <button
            type="button"
            className="spacer-ask-evans__new"
            disabled={generating || !!ctxMissing.length}
            onClick={() => {
              sessionCache.delete(cacheKey(selectedFeature, context));
              runGenerate(aiEnabled ? 'ai' : 'template');
            }}
          >
            Regenerate
          </button>
        ) : null}
      </div>

      <div className="spacer-ask-evans__messages" ref={scrollRef}>
        {!result?.markdown && !generating ? (
          <div className="spacer-ask-evans__empty">
            <div className="spacer-ask-evans__empty-avatar" aria-hidden>
              AE
            </div>
            <div className="spacer-ask-evans__empty-title">
              {selectedMeta?.title || 'Insight brief'}
            </div>
            <p className="spacer-ask-evans__empty-text">
              Pick a brief on the left, then Generate — grounded in your current Spacer Log filters.
            </p>
            {selectedMeta?.description ? (
              <p className="spacer-ask-evans__empty-hint">{selectedMeta.description}</p>
            ) : null}
            {contextHint ? (
              <p className="spacer-ask-evans__empty-hint">{contextHint}</p>
            ) : null}
          </div>
        ) : null}

        {result?.markdown ? (
          <div className="spacer-ask-evans__row spacer-ask-evans__row--bot">
            <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">AE</div>
            <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--bot">
              {showFallbackBanner ? (
                <div className="spacer-ask-evans__fallback">
                  {result.fallback
                    ? 'AI unavailable — showing template brief'
                    : 'Deterministic brief (template)'}
                  {result.error ? ` · ${result.error}` : ''}
                </div>
              ) : null}
              <SpacerLogMarkdown content={result.markdown} />
              <SpacerLogAiCitations citations={result.citations} />
              <SpacerLogAiAnswerActions
                markdown={result.markdown}
                usage={result.usage}
                feedbackTargetId={result.feedbackTargetId}
                confidence={result.confidence}
                feature={selectedFeature}
                projectId={context.projectId}
                ragSessionId={result.ragSessionId || result.sessionId}
              />
            </div>
          </div>
        ) : null}

        {generating ? (
          <div className="spacer-ask-evans__row spacer-ask-evans__row--bot">
            <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">AE</div>
            <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--bot spacer-ask-evans__thinking">
              <Spin size="small" />
              <span>Thinking… {elapsedSec}s</span>
              <button
                type="button"
                className="spacer-ask-evans__link-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <footer className="spacer-ask-evans__footer">
        {askEvansBlock ? (
          <div
            className={
              askEvansBlock.kind === 'stale'
                ? 'spacer-ask-evans__fallback spacer-ask-evans__fallback--muted'
                : 'spacer-ask-evans__fallback'
            }
          >
            <strong>{askEvansBlock.title}</strong>
            {askEvansBlock.description ? (
              <p className="spacer-ask-evans__fallback-desc">{askEvansBlock.description}</p>
            ) : null}
            {!aiEnabled ? (
              <span className="spacer-ask-evans__fallback-note"> Template briefs still work.</span>
            ) : null}
          </div>
        ) : null}
        {contextHint ? (
          <div className="spacer-ask-evans__fallback">{contextHint}</div>
        ) : null}
        <form
          className="spacer-ask-evans__composer"
          onSubmit={(e) => {
            e.preventDefault();
            runGenerate(aiEnabled ? 'ai' : 'template');
          }}
        >
          <input
            type="text"
            className="spacer-ask-evans__input"
            readOnly
            value={
              selectedMeta?.title
                ? `Generate: ${selectedMeta.title}`
                : 'Select a brief, then Generate'
            }
            disabled={generating || !!ctxMissing.length || isStub}
            aria-label="Selected insight brief"
          />
          <button
            type="button"
            className="spacer-ask-evans__send spacer-ask-evans__send--secondary"
            disabled={generating || !!ctxMissing.length}
            onClick={() => runGenerate('template')}
          >
            <span>Template</span>
          </button>
          <button
            type="submit"
            className="spacer-ask-evans__send"
            disabled={generating || !!ctxMissing.length || isStub}
          >
            <SendOutlined />
            <span>Generate</span>
          </button>
        </form>
        {quotaBar}
        <p className="spacer-ask-evans__disclaimer">
          AI can make mistakes. Check important info. Briefs use your current Spacer Log filters.
        </p>
      </footer>
    </div>
  );

  // Same chat shell: parent provides workspace + stage header; we supply sidebar + main.
  if (typeof children === 'function') {
    return children({ sidebar, main });
  }

  return (
    <div className="spacer-ask-evans__workspace">
      {sidebar}
      {main}
    </div>
  );
};

export default SpacerLogAiInsightPanel;
